import { Button as MaterialButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Role } from '../../api/user/Role';
import { AssignSchedulingWizardModal } from '../../components/common/assign-scheduling-modal';
import Actions from '../../components/layout/actions';
import BaseLayout from '../../components/layout/base-layout';
import SessionsList from '../../components/lists/sessions-list';
import CancelEventModal from '../../components/sessions/cancel-event-modal';
import MentorSessionsTable from '../../components/tables/mentor-sessions-table';
import SessionsTable from '../../components/tables/sessions-table';
import { useSessionsContext } from '../../contexts/sessions-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import { COLORS } from '../../theme/variables';
import { isMobile } from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    padding: '0 20px !important',
    [theme.breakpoints.up('xs')]: {
      padding: 0,
      marginBottom: 25,
    },
    '& h2': {
      [theme.breakpoints.down('xs')]: {
        fontSize: '22px',
        lineHeight: '40px',
      },
    },
  },
  buttonInfoIcon: {
    padding: 0,
    marginLeft: 7,
  },
  infoIcon: {
    color: COLORS.COLOR_GRAY_LIGHTENED_20,
    fontSize: 18,
  },
  modalTitle: {
    '& > div > h4': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

export function SessionsPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const location = useLocation();

  const { hasAccessToAction } = useContext(UserContext);
  const { cancelTargetSession, setCancelTargetSession, setSessions, reset } =
    useSessionsContext();

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location],
  );

  return (
    <BaseLayout user={user} fullWidth hiddenFooter={isMobile()}>
      <div className={classes.header}>
        <Typography variant='h2'>Sessions</Typography>
        <Actions
          actions={[
            {
              key: 'new-session',
              label: 'New Session',
              hidden: !hasAccessToAction('session.create'),
              component: (
                <MaterialButton
                  data-testid='button-new-session'
                  component={Link}
                  className={CLASS_TRACKING.ENTITY_ACTION}
                  to={{
                    pathname: getRoutePath(Pages.NEW_SESSIONS),
                    state: {
                      prevPath: currentPath,
                    },
                  }}
                  startIcon={<AddIcon />}
                  variant='contained'
                  color='primary'>
                  Session
                </MaterialButton>
              ),
            },
            {
              key: 'scheduling-wizard',
              label: 'Scheduling Wizard',
              hidden: !hasAccessToAction('session.wizard'),
              component: <AssignSchedulingWizardModal />,
            },
          ]}
        />
      </div>
      <Sessions />
      <CancelEventModal
        session={cancelTargetSession}
        open={!!cancelTargetSession}
        onClose={() => setCancelTargetSession(null)}
        onSuccess={() => {
          if (isMobile()) {
            if (cancelTargetSession) {
              setSessions((prev) =>
                prev.filter(
                  (session) => session.id !== cancelTargetSession?.id,
                ),
              );
            }
          } else {
            reset();
          }
        }}
      />
    </BaseLayout>
  );
}

function Sessions() {
  const { hasRole } = useContext(UserContext);

  return isMobile() ? (
    <SessionsList />
  ) : hasRole(Role.Mentor) ? (
    <MentorSessionsTable />
  ) : (
    <SessionsTable />
  );
}
